import { Box, Divider, HStack, Text, VStack } from "native-base";
import React from "react";

import FormButton from "@/components/atoms/FormButton";
import CheckoutResume from "@/components/molecules/CheckoutResume";
import Modal from "@/components/molecules/Modal";
import {
  ChangedItem,
  Order,
  QuantityChangedItem,
  WeightChangedItem,
} from "@/interfaces/domain/Order";
import { adjustCaseText, formatToCurrency, formatWeight } from "@/utils/helpers";

import { getParsedOrderInfo } from "../OrderTracking/functions";

type OrderDetailsModalProps = {
  isOpen: boolean;
  order: Order;
  onClose: () => void;
  onPressRepeatOrder: () => void;
};

const OrderDetailsModal = ({
  isOpen,
  order,
  onClose,
  onPressRepeatOrder,
}: OrderDetailsModalProps) => {
  const { store, items, shipmentValue, subtotal, total, voucherDetails, serviceTaxValue } =
    getParsedOrderInfo(order);
  const addedItems = order.itens_adicionados_separacao;
  const removedItems = order.itens_retirados;
  const changedItems = order.itens_trocados;
  const weightChangedItems = order.itens_peso_alterado;
  const quantityChangedItems = order.itens_quantidade_alterada;

  const getItemChanged = (itemId: string) => changedItems.find((i) => i.novo_item._id === itemId);
  const getItemWeightChanged = (itemId: string) =>
    weightChangedItems.find((i) => i.item_alterado._id === itemId);
  const getItemQuantityChanged = (itemId: string) =>
    quantityChangedItems.find((i) => i.item_alterado._id === itemId);

  const isChangedItem = (item: any): item is ChangedItem =>
    !!item?.item_trocado && !!item?.novo_item;
  const isWeightChangedItem = (item: any): item is WeightChangedItem =>
    !!item?.peso_final && !!item?.item_alterado;
  const isQuantityChangedItem = (item: any): item is QuantityChangedItem =>
    !!item?.quantidade_final && !!item?.item_alterado;

  const renderModifiedItem = (item: ChangedItem | WeightChangedItem | QuantityChangedItem) => {
    let changeType = "";
    let quantity = "";
    let name = "";
    let price = 0;

    if (isChangedItem(item)) {
      const isUnity = item?.item_trocado?.tipo_embalagem !== "kg";
      const currentPrice = item.item_trocado?.preco.preco_encarte
        ? item.item_trocado?.preco.preco_encarte
        : item.item_trocado?.preco.preco_original;
      const finalPrice = item.quantidade_inicial * currentPrice;
      changeType = "Itens trocados";
      quantity = isUnity ? `${item?.quantidade_inicial} und.` : `${formatWeight(item?.peso_final)}`;
      name = item?.item_trocado?.nome;
      price = finalPrice;
    }

    if (isWeightChangedItem(item)) {
      const currentPrice = item.item_alterado?.preco.preco_encarte
        ? item.item_alterado?.preco.preco_encarte
        : item.item_alterado?.preco.preco_original;
      const finalPrice = item.peso_inicial * currentPrice;
      changeType = "Peso alterado";
      quantity = formatWeight(item?.peso_inicial);
      name = item?.item_alterado?.nome;
      price = finalPrice;
    }

    if (isQuantityChangedItem(item)) {
      const currentPrice = item.item_alterado?.preco.preco_encarte
        ? item.item_alterado?.preco.preco_encarte
        : item.item_alterado?.preco.preco_original;
      const finalPrice = item.quantidade_inicial * currentPrice;
      changeType = "Quantidade alterada";
      quantity = formatWeight(item?.quantidade_inicial);
      name = item?.item_alterado?.nome;
      price = finalPrice;
    }

    return (
      <VStack>
        <Text p="1">{changeType}</Text>
        <VStack>
          <HStack p="1" py="2" alignItems="center" justifyContent="space-between" bgColor="red.300">
            <Text>{quantity}</Text>
            <VStack flex={0.8}>
              <Text isTruncated>{adjustCaseText(name)}</Text>
            </VStack>
            <Text>{formatToCurrency(price)} (atual)</Text>
          </HStack>
          <Divider position="absolute" top="50%" bgColor="black" />
        </VStack>
      </VStack>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      Header={`Pedido ${order.codigo}`}
      Body={
        <VStack>
          <CheckoutResume
            store={store}
            itemsQuantity={items?.length}
            shipmentValue={shipmentValue}
            subtotal={subtotal}
            total={total}
            voucherDetails={voucherDetails}
            serviceTaxValue={serviceTaxValue}
          />
          <Box py="2">
            <FormButton onPress={onPressRepeatOrder}>Repetir pedido</FormButton>
          </Box>
          <VStack my="4" p="4" bgColor="white.50" shadow="4" rounded="md">
            <Text fontSize="lg" bold>
              Itens
            </Text>
            <Divider />
            <VStack>
              {items.map((item) => {
                const isUnity = item?.item?.tipo_embalagem !== "kg";
                const itemChanged = getItemChanged(item.item._id);
                const itemWeightChanged = getItemWeightChanged(item.item._id);
                const itemQuantityChanged = getItemQuantityChanged(item.item._id);
                let hasChangedItem = null;
                if (itemChanged) {
                  hasChangedItem = itemChanged;
                }
                if (itemWeightChanged) {
                  hasChangedItem = itemWeightChanged;
                }
                if (itemQuantityChanged) {
                  hasChangedItem = itemQuantityChanged;
                }
                return (
                  <VStack
                    key={item?.item?._id}
                    m="1"
                    borderWidth={hasChangedItem ? 1 : 0}
                    borderColor="primary.600"
                    rounded="md">
                    {!!hasChangedItem && renderModifiedItem(hasChangedItem)}
                    <HStack
                      p="1"
                      py="2"
                      bgColor={hasChangedItem ? "info.200" : "light.50"}
                      alignItems="center"
                      justifyContent="space-between">
                      <Text>
                        {isUnity
                          ? `${item?.quantidade} und.`
                          : `${formatWeight(item.item?.peso * item.quantidade)}`}
                      </Text>
                      <VStack flex={0.8}>
                        <Text isTruncated>{adjustCaseText(item?.item?.nome)}</Text>
                      </VStack>
                      <Text>{formatToCurrency(item.preco_total)}</Text>
                    </HStack>
                  </VStack>
                );
              })}
            </VStack>
            {!!addedItems?.length && (
              <>
                <Text fontSize="lg" bold py="4">
                  Itens Adicionados
                </Text>
                <Divider />
                <VStack>
                  {addedItems.map((item) => {
                    const isUnity = item?.tipo_embalagem !== "kg";
                    return (
                      <VStack m="1" borderColor="primary.600" rounded="md">
                        <HStack
                          key={item.sku}
                          p="1"
                          py="2"
                          bgColor="light.50"
                          alignItems="center"
                          justifyContent="space-between">
                          <Text>
                            {isUnity
                              ? `${item?.quantidade} und.`
                              : `${formatWeight(item?.quantidade)}`}
                          </Text>
                          <VStack flex={0.8}>
                            <Text>{adjustCaseText(item?.nome)}</Text>
                          </VStack>
                          <Text>{formatToCurrency(item.preco_total)}</Text>
                        </HStack>
                      </VStack>
                    );
                  })}
                </VStack>
              </>
            )}
            {!!removedItems?.length && (
              <>
                <Text fontSize="lg" bold py="4">
                  Itens Removidos
                </Text>
                <Divider />
                <VStack>
                  {removedItems.map((item) => {
                    const isUnity = item?.item_retirado?.tipo_embalagem !== "kg";
                    const currentPrice = item.item_retirado?.preco.preco_encarte
                      ? item.item_retirado?.preco.preco_encarte
                      : item.item_retirado?.preco.preco_original;
                    return (
                      <VStack m="1" borderColor="primary.600" rounded="md">
                        <HStack
                          key={item.item_retirado?._id}
                          p="1"
                          py="2"
                          bgColor="light.50"
                          alignItems="center"
                          justifyContent="space-between">
                          <Text>
                            {isUnity ? `${item?.quantidade} und.` : `${formatWeight(item?.peso)}`}
                          </Text>
                          <VStack flex={0.8}>
                            <Text>{adjustCaseText(item?.item_retirado?.nome)}</Text>
                          </VStack>
                          <Text>{formatToCurrency(currentPrice)}</Text>
                        </HStack>
                      </VStack>
                    );
                  })}
                </VStack>
              </>
            )}
          </VStack>
        </VStack>
      }
    />
  );
};

export default OrderDetailsModal;
